/**
 * Shared helpers for handling values caught in `catch` blocks.
 *
 * Catch bindings are annotated `unknown` across the codebase (never `any`), so
 * these helpers are the single sanctioned way to read a message or a code off
 * a thrown value without re-introducing unsafe member access.
 */

export function getErrorMessage(error: unknown, fallback = "Something went wrong"): string {
  if (typeof error === "string") return error || fallback;
  if (error instanceof Error) return error.message || fallback;
  if (error && typeof error === "object") {
    const message = (error as { message?: unknown }).message;
    if (typeof message === "string" && message) return message;
    const errorText = (error as { error?: unknown }).error;
    if (typeof errorText === "string" && errorText) return errorText;
  }
  return fallback;
}

export function getErrorCode(error: unknown): string | undefined {
  if (error && typeof error === "object") {
    const code = (error as { code?: unknown }).code;
    if (typeof code === "string") return code;
    if (typeof code === "number") return String(code);
  }
  return undefined;
}

export function getErrorStatus(error: unknown): number | undefined {
  if (error && typeof error === "object") {
    const status = (error as { status?: unknown }).status;
    if (typeof status === "number") return status;
  }
  return undefined;
}

/** Narrow an unknown caught value to a record for read-only inspection. */
export function asErrorRecord(error: unknown): Record<string, unknown> {
  return error && typeof error === "object" ? (error as Record<string, unknown>) : {};
}
